import React, { useState, useEffect } from 'react';
import { ExternalLink, TrendingUp, Eye, Calendar, Settings, Edit3, Plus, Twitter, Facebook, Linkedin, Mail, Save, X, Upload } from 'lucide-react';
import './CompanyProfile.css';
import Dashboard from './Dashboard.js';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext.js';
import { useParams, useNavigate } from 'react-router';

const CompanyProfile = () => {
  const { selectedCompany } = useCompany();
  const {userRole} = useAuth();
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statsData, setStatsCard] = useState([]);
  const [rawData, setRawData] = useState(null);
  
  // Edit states
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingImages, setEditingImages] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [tempDescription, setTempDescription] = useState('');
  const [tempContact, setTempContact] = useState({
    twitter_url: '',
    facebook_url: '',
    linkedin_url: ''
  });
  const [tempImageUrl, setTempImageUrl] = useState('');  
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [users, setUsers] = useState([]);

  const { companyId } = useParams();
  const navigate = useNavigate();

  const fetchUsers = async () => {
    setLoading(true);
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      setLoading(false);
      return [];
    }

    try {
      const id = selectedCompany ? selectedCompany.company_id : companyId;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/list-company-users/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        console.log(data);
      } else {
        console.error('Error fetching skills:', response.statusText);
      }
    } catch (err) {
      console.error('Error fetching skills:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompany = async () => {
    if (!loading) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }
      const id = selectedCompany ? selectedCompany.company_id : companyId;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/companies/by-id/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompany(data);
        setTempDescription(data.description || '');
        setTempContact({
          twitter_url: data.twitter_url || '',
          facebook_url: data.facebook_url || '',
          linkedin_url: data.linkedin_url || ''
        });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch company details');
      }
    } catch (error) {
      setError('Failed to fetch company details');
      console.error('Error fetching company details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyStats = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }
      const id = selectedCompany ? selectedCompany.company_id : companyId;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/companies/analytics/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRawData(data);
        setStatsCard([
          {
            id: 'today',
            title: 'Today',
            value: data.today,
            comparison: data.yesterday,
            icon: Eye,
            color: 'orange'
          },
          {
            id: 'week',
            title: 'This Week',
            value: data.this_week,
            comparison: data.last_week,
            icon: Calendar,
            color: 'blue'
          },
          {
            id: 'month',
            title: 'This Month',
            value: data.this_month,
            comparison: data.last_month,
            icon: TrendingUp,
            color: 'green'
          },
          {
            id: '6months',
            title: 'Last 6 Months',
            value: data.last_six_month,
            comparison: null,
            icon: TrendingUp,
            color: 'purple'
          },
          {
            id: 'year',
            title: 'This Year',
            value: data.last_year,
            comparison: null,
            icon: Calendar,
            color: 'red'
          }
        ]);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch company analytics');
      }
    } catch (error) {
      setError('Failed to fetch company analytics');
      console.error('Error fetching company analytics:', error);
    }
  };

  // Update company description
  const updateDescription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/companies/${selectedCompany.company_id}/description`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          description: tempDescription
        })
      });

      if (response.ok) {
        setCompany(prev => ({ ...prev, description: tempDescription }));
        setEditingDescription(false);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update description');
      }
    } catch (error) {
      setError('Failed to update description');
      console.error('Error updating description:', error);
    }
  };

  // Update contact information
  const updateContact = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/companies/${selectedCompany.company_id}/contact`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tempContact)
      });

      if (response.ok) {
        setCompany(prev => ({ 
          ...prev, 
          twitter_url: tempContact.twitter_url,
          facebook_url: tempContact.facebook_url,
          linkedin_url: tempContact.linkedin_url
        }));
        setEditingContact(false);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update contact information');
      }
    } catch (error) {
      setError('Failed to update contact information');
      console.error('Error updating contact:', error);
    }
  };

  const canEdit = () => {
    return userRole != "admin";
  }

  // Upload new images
  const handleImageUrlAdd = async () => {
    if (!tempImageUrl.trim()) return;

    setUploadingImages(true);
    try {
      const token = localStorage.getItem('token');

      const id = selectedCompany ? selectedCompany.company_id : companyId;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/companies/${id}/images`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_url: tempImageUrl
        })
      });

      if (response.ok) {
        const updatedData = await response.json();
        setCompany(prev => ({ 
          ...prev, 
          images: [...(prev.images || []), { image_url: tempImageUrl }]
        }));
        setTempImageUrl('');
        setEditingImages(false);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to add image');
      }
    } catch (error) {
      setError('Failed to add image');
      console.error('Error adding image:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  // Cancel edits
  const cancelDescriptionEdit = () => {
    setTempDescription(company.description || '');
    setEditingDescription(false);
  };

  const cancelImageEdit = () => {
    setTempImageUrl('');
    setEditingImages(false);
  };

  const cancelContactEdit = () => {
    setTempContact({
      twitter_url: company.twitter_url || '',
      facebook_url: company.facebook_url || '',
      linkedin_url: company.linkedin_url || ''
    });
    setEditingContact(false);
  };

  useEffect(() => {
    if (selectedCompany || userRole == "admin") {
      console.log("fetching data");
      fetchCompany();
      fetchCompanyStats();
      fetchUsers();
    }
  }, [selectedCompany, userRole]);

  if (loading) {
    return <div className="loading-state">Loading company details...</div>;
  }
  
  if (error) {
    return <div className="error-state">Error: {error}</div>;
  }

  const formatNumber = (num) => {
    if (num === undefined) {
      return "0"
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const calculateChange = (current, previous) => {
    if (previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Dashboard page={"company"}>
      <div className="company-content">
        {/* Left Column */}
        <div className="left-column">
          {/* Company Header */}
          <div className="card company-header-card">
            <div className="company-header">
              <div className="company-header-left">
                <div className="company-logo-wrapper">
                  <div className="company-logo">
                    <span>{company.name ? company.name.charAt(0).toUpperCase() : 'C'}</span>
                  </div>
                  <div className="edit-badge">
                    <Edit3 className="edit-icon" />
                  </div>
                </div>
                
                <div className="company-details">
                  <h1 className="company-title">{company.name}</h1>
                  <a href={company.website} className="company-url" target="_blank" rel="noopener noreferrer">
                    {company.website}
                  </a>
                </div>
              </div>
              
              <div className="action-buttons">
                <button className="action-btn">
                  <Eye className="btn-icon" />
                  <span>Public View</span>
                </button>
                <button className="action-btn">
                  <Settings className="btn-icon" />
                  <span>Profile Settings</span>
                </button>
              </div>
            </div>
            
            {/* Company Stats */}
            <div className="company-stats">
              <div className="stat-item">
                <div className="stat-icon stat-icon-blue">
                  <div className="stat-dot"></div>
                </div>
                <div className="stat-content">
                  <p className="stat-label">Joined</p>
                  <p className="stat-value">
                    {new Date(company.created_at).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
              
              <div className="stat-item">
                <div className="stat-icon stat-icon-green">
                  <div className="stat-dots">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
                <div className="stat-content">
                  <p className="stat-label">Employees</p>
                  <p className="stat-value">{company.employee_range}</p>
                </div>
              </div>
              
              <div className="stat-item">
                <div className="stat-icon stat-icon-orange">
                  <div className="stat-location-dot"></div>
                </div>
                <div className="stat-content">
                  <p className="stat-label">Locations</p>
                  <p className="stat-value">{company.address?.length || 0}</p>
                </div>
              </div>
              
              <div className="stat-item">
                <div className="stat-icon stat-icon-purple">
                  <div className="stat-square"></div>
                </div>
                <div className="stat-content">
                  <p className="stat-label">Industry</p>
                  <p className="stat-value">{company.industry_name}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Company Profile Section */}
          <div className="card">
            <div className="section-header">
              <h2 className="section-title">Company Profile</h2>
              
              {canEdit() ? 
                  !editingDescription ? (
                    <button className="edit-btn" onClick={() => setEditingDescription(true)}>
                      <Edit3 className="edit-icon" />
                    </button>
                  ) : (
                    <div className="edit-actions">
                      <button className="save-btn" onClick={updateDescription}>
                        <Save className="edit-icon" />
                      </button>
                      <button className="cancel-btn" onClick={cancelDescriptionEdit}>
                        <X className="edit-icon" />
                      </button>
                    </div>
                  ) :
                  <></>
              }

            </div>
            
            <div className="company-description">
              {editingDescription ? (
                <textarea
                  value={tempDescription}
                  onChange={(e) => setTempDescription(e.target.value)}
                  className="description-textarea"
                  rows={6}
                  placeholder="Enter company description..."
                />
              ) : (
                <p>{company.description || 'No description available.'}</p>
              )}
            </div>
          </div>

          {/* Contact Section */}
          <div className="card">
            <div className="section-header">
              <h2 className="section-title">Contact</h2>
              <div className="section-actions">

                {
                  canEdit() ? 
                  !editingContact ? (
                    <button className="edit-btn" onClick={() => setEditingContact(true)}>
                      <Edit3 className="edit-icon" />
                    </button>
                  ) : (
                    <div className="edit-actions">
                      <button className="save-btn" onClick={updateContact}>
                        <Save className="edit-icon" />
                      </button>
                      <button className="cancel-btn" onClick={cancelContactEdit}>
                        <X className="edit-icon" />
                      </button>
                    </div>
                  ):
                  <></>
                }

              </div>
            </div>
            
            {editingContact ? (
              <div className="contact-edit-form">
                <div className="contact-input-group">
                  <label>Twitter URL:</label>
                  <input
                    type="url"
                    value={tempContact.twitter_url}
                    onChange={(e) => setTempContact(prev => ({ ...prev, twitter_url: e.target.value }))}
                    placeholder="https://twitter.com/yourcompany"
                    className="contact-input"
                  />
                </div>
                <div className="contact-input-group">
                  <label>Facebook URL:</label>
                  <input
                    type="url"
                    value={tempContact.facebook_url}
                    onChange={(e) => setTempContact(prev => ({ ...prev, facebook_url: e.target.value }))}
                    placeholder="https://facebook.com/yourcompany"
                    className="contact-input"
                  />
                </div>
                <div className="contact-input-group">
                  <label>LinkedIn URL:</label>
                  <input
                    type="url"
                    value={tempContact.linkedin_url}
                    onChange={(e) => setTempContact(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/company/yourcompany"
                    className="contact-input"
                  />
                </div>
              </div>
            ) : (
              <div className="contact-grid">
                {company.twitter_url && (
                  <a href={company.twitter_url} className="contact-link contact-twitter" target="_blank" rel="noopener noreferrer">
                    <Twitter className="contact-icon" />
                    <span>{company.twitter_url}</span>
                  </a>
                )}
                
                {company.facebook_url && (
                  <a href={company.facebook_url} className="contact-link contact-facebook" target="_blank" rel="noopener noreferrer">
                    <Facebook className="contact-icon" />
                    <span>{company.facebook_url}</span>
                  </a>
                )}
                
                {company.linkedin_url && (
                  <a href={company.linkedin_url} className="contact-link contact-linkedin" target="_blank" rel="noopener noreferrer">
                    <Linkedin className="contact-icon" />
                    <span>{company.linkedin_url}</span>
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="card team-members-card-content">
            <div className="section-header">
                <h2 className="section-title">Meet the team {company.name}</h2>
              </div>
            <div className="team-members-grid">
                {users.map((member) => (
                  <div key={member.id} className="team-members-member-card">
                    <img
                      src={`${process.env.REACT_APP_API_URL}/profile/get-pic/${encodeURIComponent(member.id)}`}
                      alt={member.first_name}
                      className="team-members-avatar"
                    />
                    <h4 className="team-members-member-name">
                      {member.first_name} {member.last_name}
                    </h4>
                    <p className="team-members-member-title">
                        <span className={member.is_active ? "status-badge status-live" : "status-draft"} >
                          {member.is_active ? "Enabled" : "Disabled"}
                        </span>
                    </p>
                    <p className="team-members-member-title">
                      {member.email}
                    </p>
                    <p className="team-members-member-title">
                      Last Seen {formatDate(member.last_time_online)}
                    </p>
                    {/* <div className="team-members-social-links">
                      <a
                        href={member.socialLinks.instagram}
                        className="team-members-social-link"
                      >
                        <Instagram size={20} />
                      </a>
                      <a
                        href={member.socialLinks.linkedin}
                        className="team-members-social-link"
                      >
                        <Linkedin size={20} />
                      </a>
                    </div> */}
                  </div>
                ))}
             </div> 
          </div>

          {/* Working at Company Section */}
          <div className="working-at-nomad">
            <section className="work-section">
              <div className="section-header">
                <h2 className="section-title">Working at {company.name}</h2>
                <div className="section-actions">
                  
                  {canEdit() ? !editingImages ? (
                    <button className="action-btn" onClick={() => setEditingImages(true)}>
                      <Plus className="btn-icon" />
                    </button>
                  ) : (
                    <div className="edit-actions">
                      <button 
                        className="save-btn" 
                        onClick={handleImageUrlAdd}
                        disabled={uploadingImages || !tempImageUrl.trim()}
                      >
                        <Save className="edit-icon" />
                        {uploadingImages && <span className="upload-spinner">⟳</span>}
                      </button>
                      <button className="cancel-btn" onClick={cancelImageEdit}>
                        <X className="edit-icon" />
                      </button>
                    </div>
                  ) : <></>}

                  {canEdit() ? 
                  <button className="action-btn">
                    <Edit3 className="btn-icon" />
                  </button> : <></>}
                </div>
              </div>

              {editingImages && (
                <div className="image-url-form">
                  <div className="url-input-group">
                    <label htmlFor="image-url">Image URL:</label>
                    <input
                      id="image-url"
                      type="url"
                      value={tempImageUrl}
                      onChange={(e) => setTempImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="image-url-input"
                      disabled={uploadingImages}
                    />
                  </div>
                  {tempImageUrl && (
                    <div className="image-preview-container">
                      <p className="preview-label">Preview:</p>
                      <img
                        src={tempImageUrl}
                        alt="Image preview"
                        className="url-image-preview"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                        onLoad={(e) => {
                          e.target.style.display = 'block';
                          e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <div className="invalid-image-message" style={{ display: 'none' }}>
                        Invalid image URL or image failed to load
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {company.images && company.images.length > 0 && (
                <div className="image-gallery">
                  <div className="main-image">
                    <div className="placeholder-image main-placeholder">
                      <div className="image-content">
                        <img
                          src={company.images[0].image_url}
                          alt="Company workspace"
                          className="image-preview"
                          crossOrigin="anonymous"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="side-images">
                    {company.images.slice(1, Math.min(4, company.images.length)).map((img, index) => (
                      <div key={index} className="small-image">
                        <div className="placeholder-image small-placeholder">
                          <div className="image-content">
                            <img
                              src={img.image_url}
                              alt={`Company image ${index + 2}`}
                              className="image-preview"
                              crossOrigin="anonymous"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {company.images && company.images.length > 4 && (
                <div className="additional-images-section">
                  <h4 className="additional-images-title">
                    More Images ({company.images.length - 4})
                  </h4>
                  <div className="thumbnails-grid">
                    {company.images.slice(4).map((img, index) => (
                      <div 
                        key={index + 4} 
                        className={`thumbnail-item ${selectedThumbnail === index + 4 ? 'selected' : ''}`}
                        onClick={() => setSelectedThumbnail(selectedThumbnail === index + 4 ? null : index + 4)}
                      >
                        <img
                          src={img.image_url}
                          alt={`Company image ${index + 5}`}
                          className="thumbnail-image"
                          crossOrigin="anonymous"
                        />
                        <div className="thumbnail-overlay">
                          <span className="thumbnail-index">{index + 5}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Expanded View for Selected Thumbnail */}
                  {selectedThumbnail !== null && company.images[selectedThumbnail] && (
                    <div className="thumbnail-expanded-view">
                      <div className="expanded-image-container">
                        <img
                          src={company.images[selectedThumbnail].image_url}
                          alt={`Expanded view of image ${selectedThumbnail + 1}`}
                          className="expanded-image"
                          crossOrigin="anonymous"
                        />
                        <button 
                          className="close-expanded"
                          onClick={() => setSelectedThumbnail(null)}
                        >
                          <X className="close-icon" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </section>
          </div>

        </div>

        {/* Right Column */}
        <div className="right-column">
          {/* Company Views Stats */}
          <div className="card">
            <div className="company-stats-container">
              <div className="stats-header">
                <h3 className="stats-title">COMPANY VIEWS</h3>
              </div>
              
              <div className="stats-grid">
                {statsData.map((stat) => {
                  const change = stat.comparison ? calculateChange(stat.value, stat.comparison) : null;
                  const IconComponent = stat.icon;
                  
                  return (
                    <div key={stat.id} className="stat-card">
                      <div className={`stat-icon stat-icon-${stat.color}`}>
                        <IconComponent className="icon" />
                      </div>
                      <div className="stat-content">
                        <p className="stat-title">{stat.title}</p>
                        <div className="stat-value-container">
                          <span className="stat-value">{formatNumber(stat.value)}</span>
                          {change !== null && (
                            <span className={`stat-change ${change >= 0 ? 'positive' : 'negative'}`}>
                              {change > 0 ? '+' : ''}{change}%
                            </span>
                          )}
                        </div>
                        {stat.comparison && (
                          <p className="stat-comparison">
                            vs {formatNumber(stat.comparison)} {stat.id === 'today' ? 'yesterday' : 
                                stat.id === 'week' ? 'last week' : 'last month'}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="detailed-stats">
                <div className="detailed-stat-row">
                  <span className="detailed-label">Yesterday</span>
                  <span className="detailed-value">{rawData?.yesterday || 0}</span>
                </div>
                <div className="detailed-stat-row">
                  <span className="detailed-label">Last Week</span>
                  <span className="detailed-value">{rawData?.last_week || 0}</span>
                </div>
                <div className="detailed-stat-row">
                  <span className="detailed-label">Last Month</span>
                  <span className="detailed-value">{rawData?.last_month || 0}</span>
                </div>
              </div>
              
              <button className="view-analytics-btn">
                <span>View detailed analytics</span>
                <ExternalLink className="btn-icon" />
              </button>
            </div>
          </div>

          {/* Office Locations */}
          <div className="card">
            <div className="section-header">
              <h3 className="section-title">Office Locations</h3>
              <div className="section-actions">
                <button className="edit-btn">
                  <Plus className="edit-icon" />
                </button>
                <button className="edit-btn">
                  <Edit3 className="edit-icon" />
                </button>
              </div>
            </div>
            
            {company.address && company.address.map((addy, index) => (
              <div key={index} className="location-item">
                <div className="location-info">
                  <div className="us-flag"></div>
                  <div>
                    <p className="location-name">{addy.country}</p>
                  </div>
                </div>
                {addy.is_headquarters && <span className="location-badge">Head Quarters</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default CompanyProfile;